import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import toast from 'react-hot-toast'
import { getErrorMessage } from '@/utils/errorHandling'

export default function ReferralGatePage() {
  const navigate = useNavigate()
  const [referralCode, setReferralCode] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    // Only redirect if auth check is complete and user is authenticated
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, isLoading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!referralCode.trim()) {
      toast.error('Please enter a referral code')
      return
    }

    setIsValidating(true)

    try {
      const response = await api.post('/auth/validate-referral', {
        referral_code: referralCode.trim(),
      })

      if (response.data.valid) {
        // Store referral code in session storage for signup
        sessionStorage.setItem('validated_referral', referralCode.trim())
        toast.success('Welcome! You can now create an account.')
        navigate('/signup')
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
      setReferralCode('')
    } finally {
      setIsValidating(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-olive-50 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-charcoal-900 mb-2">KeffiyehQL</h1>
            <p className="text-lg text-olive-700">Letter Archive Platform</p>
          </div>

          <div className="bg-olive-100 rounded-lg p-4 mb-8">
            <p className="text-sm text-olive-800 text-center">
              Access to KeffiyehQL requires a valid referral code from an existing member.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="referralCode"
                className="block text-sm font-medium text-charcoal-700 mb-2"
              >
                Enter Referral Code
              </label>
              <input
                id="referralCode"
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="e.g., USER20250802ABCDEF"
                className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent text-center font-mono text-lg"
                disabled={isValidating}
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={isValidating || !referralCode.trim()}
              className="w-full bg-olive-600 text-white py-3 rounded-lg font-semibold hover:bg-olive-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? 'Validating...' : 'Enter Platform'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-charcoal-600 mb-2">Already have an account?</p>
            <a href="/login" className="text-olive-600 hover:text-olive-700 font-medium">
              Sign in here
            </a>
          </div>

          <div className="mt-8 pt-8 border-t border-olive-200">
            <div className="text-center">
              <h3 className="text-sm font-semibold text-charcoal-700 mb-2">How it works:</h3>
              <ul className="text-xs text-charcoal-600 space-y-1">
                <li>• Get a referral code from an existing member</li>
                <li>• Codes are unique and valid for 24 hours</li>
                <li>• After validation, you can create your account</li>
                <li>• Once registered, you'll get your own daily referral code</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-charcoal-500">
              A crisis-first platform for preserving important communications
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
